import type React from 'react'
import { AlertTriangle, Bell, Clock, ShoppingCart, UserPlus } from 'lucide-react'
import type { TFunction } from 'i18next'

export type NotificationItem = {
  id: number
  type: string
  title: string
  message: string
  read: boolean
  action_url?: string | null
  created_at: string
  branch_id?: string | null
  branch_name?: string | null
}

export const TAB_ALL = '1'
export const TAB_UNREAD = '2'
export const TAB_ALERTS = '3'

export const EXPIRY_ALERT_TYPES = ['expiry_alert', 'expiry_near_alert', 'expiry_expired_alert'] as const

export const notificationColorMap: Record<string, string> = {
  danger:
    'border border-semantic-danger-border bg-semantic-danger-soft text-semantic-danger',
  success:
    'border border-semantic-success-border bg-semantic-success-soft text-semantic-success',
  warning:
    'border border-semantic-warning-border bg-semantic-warning-soft text-semantic-warning',
  info: 'border border-semantic-info-border bg-semantic-info-soft text-semantic-info',
  primary: 'border border-border-subtle bg-muted text-foreground',
}

export const notificationIconMap: Record<string, React.ElementType> = {
  low_stock: AlertTriangle,
  expiry_alert: Clock,
  expiry_near_alert: Clock,
  expiry_expired_alert: AlertTriangle,
  new_sale: ShoppingCart,
  new_customer: UserPlus,
  default: Bell,
}

export function getNotificationColor(type: string): keyof typeof notificationColorMap {
  switch (type) {
    case 'low_stock':
    case 'expiry_expired_alert':
      return 'danger'
    case 'expiry_near_alert':
      return 'warning'
    case 'expiry_alert':
      return 'danger'
    case 'new_sale':
      return 'success'
    case 'new_customer':
      return 'info'
    default:
      return 'primary'
  }
}

export function tabToApiParam(tab: string): 'all' | 'unread' | 'alerts' {
  if (tab === TAB_UNREAD) return 'unread'
  if (tab === TAB_ALERTS) return 'alerts'
  return 'all'
}

export function formatNotificationTime(dateString: string, t: TFunction): string {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (minutes < 1) return t('Just now')
  if (minutes < 60) return `${minutes} ${t('minutes ago')}`
  if (hours < 24) return `${hours} ${t('hours ago')}`
  return `${days} ${t('days ago')}`
}

export function filterNotificationsByTab(
  items: NotificationItem[],
  tab: string,
  useClientFilter: boolean
): NotificationItem[] {
  if (!useClientFilter) return items
  if (tab === TAB_UNREAD) return items.filter((n) => !n.read)
  if (tab === TAB_ALERTS) {
    return items.filter(
      (n) => n.type === 'low_stock' || EXPIRY_ALERT_TYPES.includes(n.type as (typeof EXPIRY_ALERT_TYPES)[number])
    )
  }
  return items
}
